import { useEffect, useMemo, useState } from 'react';

import * as toast from 'utils/toast';
import { hasKey } from 'utils/object';
import { downloadCSV } from 'utils/csv';
import { parse } from 'utils/queryParam';
import { handleError } from 'utils/errorHandler';
import { startCase } from 'utils/string';
import { getFormattedDate, getMonthsFirstDate } from 'utils/date';

import useQuery from 'hooks/useQuery';

import { LEAVE_ISSUER_CHANGE_STATUS, requestPillStatus } from 'constants/changeLeaveIssuer';
import { mixPanelEvents } from 'constants/mixPanel';
import { DISPLAY_DATE_FORMAT } from 'constants/date';

import * as mixpanel from 'services/mixPanel';
import * as leaveIssuerChangeRequestService from 'services/leaveIssuerChangeRequest';

import type {
  ChangeLeaveIssuerFilters,
  ChangeLeaveIssuerListProps,
  ChangeLeaveIssuerQuery,
  LeaveIssuerRequestSummary,
  UseChangeLeaveIssuerListResult,
} from './interfaces';
import type { LeaveIssuerChangeRequestItem } from 'interface/issuer';
import type { QueryObject } from 'interface/queryParam';
import type { ServiceId } from 'interface/service';

export function useChangeLeaveIssuerList(props: ChangeLeaveIssuerListProps): UseChangeLeaveIssuerListResult {
  const [rawQuery, setQuery] = useQuery(props) as [ChangeLeaveIssuerQuery, (nextQuery: QueryObject) => void];
  const query = rawQuery;

  const defaultFilters = useMemo<Pick<ChangeLeaveIssuerFilters, 'startDate' | 'endDate'>>(() => {
    const currentDate = getFormattedDate();

    return {
      startDate: getMonthsFirstDate(currentDate),
      endDate: currentDate,
    };
  }, []);

  const queryParams = parse(props.location.search) as ChangeLeaveIssuerQuery;
  const appliedFilters = useMemo<ChangeLeaveIssuerFilters>(
    () => ({
      ...queryParams,
      startDate: query.startDate || defaultFilters.startDate,
      endDate: query.endDate || defaultFilters.endDate,
    }),
    [defaultFilters.endDate, defaultFilters.startDate, query.endDate, query.startDate, queryParams]
  );

  const [isLoading, setIsLoading] = useState(true);
  const [leaveIssuerChangeRequestList, setLeaveIssuerChangeRequestList] = useState<LeaveIssuerChangeRequestItem[]>([]);
  const [requestSummary, setRequestSummary] = useState<LeaveIssuerRequestSummary>({
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [selectedRequestIds, setSelectedRequestIds] = useState<ServiceId[]>([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [rowData, setRowData] = useState<LeaveIssuerChangeRequestItem | null>(null);
  const [isRejectingRequest, setIsRejectingRequest] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filters, setFilters] = useState<ChangeLeaveIssuerFilters>({
    ...queryParams,
    startDate: query.startDate || defaultFilters.startDate,
    endDate: query.endDate || defaultFilters.endDate,
  });
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(false);
  const [columnToSortBy, setColumnToSortBy] = useState('dateOfRequest');

  const isStartDateChanged = getFormattedDate(filters.startDate) !== getFormattedDate(defaultFilters.startDate);
  const isEndDateChanged = getFormattedDate(filters.endDate) !== getFormattedDate(defaultFilters.endDate);

  const applyFilter = (filter: ChangeLeaveIssuerFilters) => {
    setQuery({ ...query, ...filter });
  };

  const getDataToShow = (data: LeaveIssuerChangeRequestItem[], status?: string) => {
    if (!status) {
      return data;
    }

    if (status === requestPillStatus.APPROVED) {
      return data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.APPROVED);
    }

    if (status === requestPillStatus.PENDING) {
      return data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.PENDING);
    }

    if (status === requestPillStatus.REJECTED) {
      return data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.REJECTED);
    }

    return data;
  };

  const trackRequestPillSelection = (status?: string) => {
    if (!status) {
      const { all: selectAllPills } = mixPanelEvents.select.leaveIssuerChangeRequest.list.pills;

      mixpanel.trackEvent(selectAllPills);
      return;
    }

    if (status === requestPillStatus.APPROVED) {
      const { approved: selectApprovedPills } = mixPanelEvents.select.leaveIssuerChangeRequest.list.pills;

      mixpanel.trackEvent(selectApprovedPills);
      return;
    }

    if (status === requestPillStatus.PENDING) {
      const { pending: selectPendingPills } = mixPanelEvents.select.leaveIssuerChangeRequest.list.pills;

      mixpanel.trackEvent(selectPendingPills);
      return;
    }

    if (status === requestPillStatus.REJECTED) {
      const { rejected: selectRejectedPills } = mixPanelEvents.select.leaveIssuerChangeRequest.list.pills;

      mixpanel.trackEvent(selectRejectedPills);
      return;
    }
  };

  const fetchLeaveIssuerChangeRequestList = async () => {
    try {
      setIsLoading(true);

      const { data } = await leaveIssuerChangeRequestService.fetchAll(filters);

      setRequestSummary({
        pending: data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.PENDING).length,
        approved: data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.APPROVED).length,
        rejected: data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.REJECTED).length,
      });
      setLeaveIssuerChangeRequestList(data);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const { list: viewChangeRequestList } = mixPanelEvents.view.leaveIssuerChangeRequest;

    mixpanel.trackEvent(viewChangeRequestList);
  }, []);

  useEffect(() => {
    setQuery({
      ...query,
      status: hasKey(filters, 'status') ? query.status : requestPillStatus.PENDING,
      startDate: query.startDate || defaultFilters.startDate,
      endDate: query.endDate || defaultFilters.endDate,
    });

    void (async () => {
      try {
        setIsLoading(true);

        const { data } = await leaveIssuerChangeRequestService.fetchAll(filters);

        setRequestSummary({
          pending: data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.PENDING).length,
          approved: data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.APPROVED).length,
          rejected: data.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.REJECTED).length,
        });
        setLeaveIssuerChangeRequestList(data);
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    })();
    // This list is intentionally refetched only when the applied date query changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilters.endDate, defaultFilters.startDate, query.endDate, query.startDate]);

  useEffect(() => {
    trackRequestPillSelection(queryParams.status);
  }, [queryParams.status]);

  const dataToShow = useMemo(
    () => getDataToShow(leaveIssuerChangeRequestList, queryParams.status),
    [leaveIssuerChangeRequestList, queryParams.status]
  );

  useEffect(() => {
    // no-op placeholder removed by derived dataToShow
  }, []);

  const visibleSelectedRequestIds = useMemo(
    () => selectedRequestIds.filter((selectedRequestId) => dataToShow.some(({ id }) => id === selectedRequestId)),
    [dataToShow, selectedRequestIds]
  );

  const areAllPendingRequestsSelected = useMemo(() => {
    const selectedRequestIdsSet = new Set(visibleSelectedRequestIds);
    const pendingRequestIds = dataToShow
      .filter(({ status }) => status === LEAVE_ISSUER_CHANGE_STATUS.PENDING)
      .map(({ id }) => id);

    return pendingRequestIds.length > 0 && pendingRequestIds.every((id) => selectedRequestIdsSet.has(id));
  }, [dataToShow, visibleSelectedRequestIds]);

  const handleReset = () => {
    const { resetButton } = mixPanelEvents.select.leaveIssuerChangeRequest.list;

    if (!isFilterApplied) {
      return;
    }

    const nextFilters: ChangeLeaveIssuerFilters = {
      status: requestPillStatus.PENDING,
      ...defaultFilters,
    };

    setIsFilterApplied(false);
    setFilters(nextFilters);
    applyFilter(nextFilters);

    mixpanel.trackEvent(resetButton);
  };

  const handleApplyFilter = () => {
    const { applyFilterButton } = mixPanelEvents.select.leaveIssuerChangeRequest.list;

    if (!isFilterChanged) {
      return;
    }

    applyFilter(filters);
    setIsFilterChanged(false);
    setIsFilterApplied(true);

    mixpanel.trackEvent(applyFilterButton);
  };

  const handleExportClick = async () => {
    try {
      setIsExporting(true);

      const { exportCSVButton } = mixPanelEvents.select.leaveIssuerChangeRequest.list;
      const { data: blob } = await leaveIssuerChangeRequestService.exportAll(appliedFilters);
      const formattedStartDate = getFormattedDate(appliedFilters.startDate, DISPLAY_DATE_FORMAT);
      const formattedEndDate = getFormattedDate(appliedFilters.endDate, DISPLAY_DATE_FORMAT);
      const fileName =
        `${query.status ? `${startCase(query.status)} ` : ''}` +
        `Leave Issuer Change Requests (${formattedStartDate} - ${formattedEndDate}).csv`;

      downloadCSV(blob, fileName);
      mixpanel.trackEvent(exportCSVButton);
    } catch (error) {
      handleError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRejectClick = async (values: import('interface/issuer').RejectChangeRequest) => {
    if (!rowData) {
      return;
    }

    const user = rowData.user;
    const status = LEAVE_ISSUER_CHANGE_STATUS.REJECTED;
    const rejectRequestByPeopleManagement = mixPanelEvents.reject.leaveIssuerChangeRequest.byPeopleManagement;

    try {
      setIsRejectingRequest(true);

      await leaveIssuerChangeRequestService.updateLeaveIssuerChangeRequest(user.id, {
        status,
        comment: values.rejectionReason,
      });

      toast.success({
        title: 'Success',
        message: `Leave issuer change request of ${user.fullname} has been rejected.`,
      });

      mixpanel.trackEvent(rejectRequestByPeopleManagement);
      await fetchLeaveIssuerChangeRequestList();
    } catch (error) {
      handleError(error);
    } finally {
      setIsRejectingRequest(false);
      setIsModalOpen(false);
    }
  };

  const handleApproveClick = async (
    user: LeaveIssuerChangeRequestItem['user'],
    status: string,
    newLeaveIssuer: LeaveIssuerChangeRequestItem['newLeaveIssuer'],
    setIsApprovingRequest: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    try {
      setIsApprovingRequest(true);

      const approveRequestByPeopleManagement = mixPanelEvents.approve.leaveIssuerChangeRequest.byPeopleManagement;

      await leaveIssuerChangeRequestService.updateLeaveIssuerChangeRequest(user.id, { status });

      toast.success({
        title: 'Success',
        message: `Leave issuer of ${user.fullname} has been updated to ${newLeaveIssuer.fullname}.`,
      });

      mixpanel.trackEvent(approveRequestByPeopleManagement);
      await fetchLeaveIssuerChangeRequestList();
    } catch (error) {
      handleError(error);
    } finally {
      setIsApprovingRequest(false);
    }
  };

  const handleBulkApproveClick = async () => {
    try {
      setIsBulkApproving(true);

      const { bulkByPeopleManagement: bulkApproveByPeopleManagement } = mixPanelEvents.approve.leaveIssuerChangeRequest;
      const body = {
        requestIds: visibleSelectedRequestIds,
        status: LEAVE_ISSUER_CHANGE_STATUS.APPROVED,
      };

      const {
        data: { requestIds: updatedIds },
      } = await leaveIssuerChangeRequestService.bulkUpdateLeaveIssuerChangeRequest(body);

      if (!updatedIds.length) {
        toast.error({
          title: 'Error',
          message: 'Cannot approve leave issuer change request for self.',
        });
      } else {
        toast.success({
          title: 'Success',
          message: 'Leave issuer change requests have been approved.',
        });

        mixpanel.trackEvent(bulkApproveByPeopleManagement);
        await fetchLeaveIssuerChangeRequestList();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsBulkApproving(false);
    }
  };

  const onChangeSelectAllCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (e.target.checked) {
      setSelectedRequestIds(
        dataToShow.filter((item) => item.status === LEAVE_ISSUER_CHANGE_STATUS.PENDING).map(({ id }) => id)
      );
      setIsSelectAllChecked(true);

      return;
    }

    setSelectedRequestIds([]);
    setIsSelectAllChecked(false);
  };

  const isStatusApprovedOrRejected = useMemo(
    () =>
      [LEAVE_ISSUER_CHANGE_STATUS.APPROVED.toUpperCase(), LEAVE_ISSUER_CHANGE_STATUS.REJECTED.toUpperCase()].includes(
        queryParams.status || ''
      ),
    [queryParams.status]
  );

  const isChecked =
    Boolean(isSelectAllChecked && visibleSelectedRequestIds.length) ||
    (areAllPendingRequestsSelected && !isStatusApprovedOrRejected);

  return {
    filters,
    setFilters,
    isLoading,
    requestSummary,
    dataToShow,
    selectedRequestIds: visibleSelectedRequestIds,
    setSelectedRequestIds,
    isBulkApproving,
    setIsSelectAllChecked,
    setRowData,
    isRejectingRequest,
    isExporting,
    isModalOpen,
    setIsModalOpen,
    isFilterChanged,
    setIsFilterChanged,
    isFilterApplied,
    isStartDateChanged,
    isEndDateChanged,
    isAscendingOrdered,
    setIsAscendingOrdered,
    columnToSortBy,
    setColumnToSortBy,
    handleReset,
    handleApplyFilter,
    handleExportClick,
    handleRejectClick,
    handleApproveClick,
    handleBulkApproveClick,
    onChangeSelectAllCheckbox,
    isChecked,
    isStatusApprovedOrRejected,
  };
}
